import React, { useState, useEffect } from "react";
import {
  StyleSheet,
  View,
  Modal,
  TouchableOpacity,
  ScrollView,
  KeyboardAvoidingView,
  Platform,
} from "react-native";
import { MessageType, ISendMessageRequest, MessageModel } from "common247";
import { RecipientSelector, ISelectedRecipient } from "./RecipientSelector";
import { useThemeColor } from "@/hooks/useThemeColor";
import { IconSymbol } from "@/components/ui/IconSymbol";
import { ThemedButton } from "@/components/ThemedButton";
import { ThemedInput } from "@/components/ThemedInput";
import { ThemedText } from "@/components/ThemedText";
import { BrandColors } from "@/constants/themes/base";

/**
 * Properties for the ComposeMessageModal component.
 */
export interface IComposeMessageModalProps {
  visible: boolean;
  onClose: () => void;
  onSend: (req: ISendMessageRequest) => Promise<MessageModel | null>;
  initialRecipient?: ISelectedRecipient | null;
  initialSubject?: string;
  initialType?: MessageType;
}

/**
 * Modal dialog for composing and sending an internal message.
 * Supports choosing from all contacts and groups (owned and shared), specifying subject and body.
 * Message type is dictated by the system (defaulting to "General") and cannot be changed by the user.
 * Adheres strictly to Sharp Corners and Archival Heirloom standards.
 */
export function ComposeMessageModal({
  visible,
  onClose,
  onSend,
  initialRecipient,
  initialSubject,
  initialType,
}: IComposeMessageModalProps) {
  const [recipient, setRecipient] = useState<ISelectedRecipient | null>(null);
  const [type, setType] = useState<MessageType>("general");
  const [subject, setSubject] = useState<string>("");
  const [body, setBody] = useState<string>("");

  const [errors, setErrors] = useState<{
    recipient?: string;
    subject?: string;
    body?: string;
    general?: string;
  }>({});
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const cardBg = useThemeColor({}, "card");
  const textColor = useThemeColor({}, "text");
  const borderColor = useThemeColor({}, "border");
  const subtextColor = useThemeColor({}, "textMuted");

  useEffect(() => {
    if (visible) {
      setRecipient(initialRecipient ?? null);
      setType(initialType ?? "general");
      setSubject(initialSubject ?? "");
      setBody("");
      setErrors({});
    }
  }, [visible, initialRecipient, initialSubject, initialType]);

  const resetForm = () => {
    setRecipient(null);
    setType(initialType ?? "general");
    setSubject("");
    setBody("");
    setErrors({});
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async () => {
    const newErrors: { recipient?: string; subject?: string; body?: string } = {};

    if (!recipient) {
      newErrors.recipient = "Please select a recipient (contact or group).";
    }
    if (!subject.trim()) {
      newErrors.subject = "Subject cannot be empty.";
    }
    if (!body.trim()) {
      newErrors.body = "Message body cannot be empty.";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    try {
      const payload: ISendMessageRequest = {
        targetType: recipient!.targetType,
        targetId: recipient!.targetId,
        targetName: recipient!.targetName,
        recipientUserId: recipient!.recipientUserId,
        type: initialType ?? "general",
        subject: subject.trim(),
        body: body.trim(),
      };

      const result = await onSend(payload);
      if (result) {
        handleClose();
      } else {
        setErrors({ general: "Failed to send message. Please try again." });
      }
    } catch (err: any) {
      setErrors({ general: err.message || "An unexpected error occurred." });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      visible={visible}
      animationType="fade"
      transparent
      onRequestClose={handleClose}
    >
      <View style={styles.modalBackdrop}>
        <KeyboardAvoidingView
          behavior={Platform.OS === "ios" ? "padding" : undefined}
          style={styles.keyboardAvoid}
        >
          <View
            style={[
              styles.modalCard,
              {
                backgroundColor: cardBg,
                borderColor: borderColor,
              },
            ]}
          >
            {/* Header */}
            <View style={[styles.headerRow, { borderBottomColor: borderColor }]}>
              <View style={styles.headerTitleRow}>
                <IconSymbol size={20} name="envelope.badge" color={textColor} />
                <ThemedText style={[styles.headerTitle, { color: textColor }]}>
                  {initialSubject ? "Reply to Message" : "New Message"}
                </ThemedText>
              </View>
              <TouchableOpacity
                onPress={handleClose}
                style={styles.closeButton}
                accessibilityLabel="Close message composer"
              >
                <ThemedText style={[styles.closeIcon, { color: subtextColor }]}>✕</ThemedText>
              </TouchableOpacity>
            </View>

            {/* Scrollable Form Content */}
            <ScrollView
              style={styles.bodyScroll}
              contentContainerStyle={styles.bodyContent}
              keyboardShouldPersistTaps="handled"
            >
              {errors.general ? (
                <View style={styles.generalErrorBanner}>
                  <ThemedText style={styles.generalErrorText}>{errors.general}</ThemedText>
                </View>
              ) : null}

              {/* Recipient Picker */}
              <RecipientSelector
                selectedRecipient={recipient}
                onSelect={(sel) => {
                  setRecipient(sel);
                  if (errors.recipient) setErrors((prev) => ({ ...prev, recipient: undefined }));
                }}
                error={errors.recipient}
              />

              {/* Subject */}
              <View style={styles.fieldBlock}>
                <ThemedText style={[styles.fieldLabel, { color: textColor }]}>
                  Subject <ThemedText style={styles.requiredAsterisk}>*</ThemedText>
                </ThemedText>
                <ThemedInput
                  placeholder="Enter message subject..."
                  value={subject}
                  onChangeText={(val) => {
                    setSubject(val);
                    if (errors.subject) setErrors((prev) => ({ ...prev, subject: undefined }));
                  }}
                  error={errors.subject}
                />
              </View>

              {/* Body */}
              <View style={styles.fieldBlock}>
                <ThemedText style={[styles.fieldLabel, { color: textColor }]}>
                  Message Body <ThemedText style={styles.requiredAsterisk}>*</ThemedText>
                </ThemedText>
                <ThemedInput
                  placeholder="Write your internal message here..."
                  value={body}
                  onChangeText={(val) => {
                    setBody(val);
                    if (errors.body) setErrors((prev) => ({ ...prev, body: undefined }));
                  }}
                  multiline
                  numberOfLines={5}
                  error={errors.body}
                />
              </View>
            </ScrollView>

            {/* Footer Actions */}
            <View style={[styles.footerRow, { borderTopColor: borderColor }]}>
              <ThemedButton
                title="Cancel"
                variant="minimal"
                onPress={handleClose}
                disabled={isSubmitting}
              />

              <ThemedButton
                title={isSubmitting ? "Sending..." : "Send Message"}
                variant="primary"
                onPress={handleSubmit}
                disabled={isSubmitting}
                loading={isSubmitting}
              />
            </View>
          </View>
        </KeyboardAvoidingView>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  modalBackdrop: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.65)",
    justifyContent: "center",
    alignItems: "center",
    padding: 16,
  },
  keyboardAvoid: {
    width: "100%",
    maxWidth: 580,
  },
  modalCard: {
    borderRadius: 0,
    borderWidth: 1,
    maxHeight: "90%",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 8,
    elevation: 6,
    overflow: "hidden",
  },
  headerRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderBottomWidth: 1,
  },
  headerTitleRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
  },
  headerTitle: {
    fontSize: 16,
    fontWeight: "700",
  },
  closeButton: {
    padding: 4,
  },
  closeIcon: {
    fontSize: 16,
    fontWeight: "700",
  },
  bodyScroll: {
    maxHeight: 520,
  },
  bodyContent: {
    padding: 12,
  },
  generalErrorBanner: {
    backgroundColor: "rgba(171, 23, 23, 0.1)",
    borderWidth: 1,
    borderColor: BrandColors.beetRed.hex,
    borderRadius: 0,
    padding: 8,
    marginBottom: 10,
  },
  generalErrorText: {
    color: BrandColors.beetRed.hex,
    fontSize: 12,
    fontWeight: "600",
  },
  fieldBlock: {
    marginBottom: 10,
    zIndex: 1,
  },
  fieldLabel: {
    fontSize: 11,
    lineHeight: 14,
    fontWeight: "600",
    marginBottom: 2,
    opacity: 0.75,
    letterSpacing: 0.2,
  },
  requiredAsterisk: {
    color: BrandColors.beetRed.hex,
  },
  footerRow: {
    flexDirection: "row",
    justifyContent: "flex-end",
    alignItems: "center",
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderTopWidth: 1,
    gap: 10,
  },
});
